import React, { useEffect, useRef } from 'react';
import { startStripeCheckout } from '../utils/upgradeFlow';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MobileDesktopBanner from '../components/MobileDesktopBanner';
import { foundersFor } from '../config/foundersDiscount';
import './MpjePrep.css';

// Placeholder counts — Phase 1 content ingestion (Abood & Burns, Reiss & Hall, Dr. C TX)
// populates mpje_content and these numbers get wired to real chunk/boss counts.
const DOMAINS = [
  // Counts derived from services/mpje_content.py + the compliance board
  // (mpje_map_generator, 2026-06-12) — MAP-LITERAL semantics matching the
  // NAPLEX/PTCB pages: lessons = topic groups (one teaching lesson each),
  // synthesis = inter-group cross-check links, bosses = fightable boss nodes
  // the board renders for the domain (one group boss per group + one tier
  // boss per link; the single board-center apothecarius is uncounted),
  // rounds = clinical scenario rounds.
  { name: 'Controlled Substances', color: '#f59e0b', lessons: 22, synthesis: 21, bosses: 43, rounds: 3 },
  { name: 'Federal Law', color: '#64748b', lessons: 38, synthesis: 37, bosses: 75, rounds: 3 },
  { name: 'Prescription Requirements', color: '#6366f1', lessons: 18, synthesis: 17, bosses: 35, rounds: 3 },
  { name: 'Pharmacy Operations', color: '#14b8a6', lessons: 28, synthesis: 27, bosses: 55, rounds: 3 },
  { name: 'Patient Safety', color: '#f43f5e', lessons: 22, synthesis: 21, bosses: 43, rounds: 3 },
  { name: 'Regulatory Structure', color: '#78716c', lessons: 36, synthesis: 35, bosses: 71, rounds: 3 },
];

const CheckIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function MpjePrep() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const observerRef = useRef(null);
  const isAliasRoute = location.pathname === '/mpje';
  const canonicalUrl = 'https://debono.ai/mpje-prep';
  const robotsValue = isAliasRoute ? 'noindex, follow' : 'index, follow';

  useEffect(() => {
    const ref = searchParams.get('ref');
    const utmSource = searchParams.get('utm_source');
    const utmCampaign = searchParams.get('utm_campaign');
    if (ref || utmSource || utmCampaign) {
      try {
        localStorage.setItem('debono_ref', JSON.stringify({
          ref,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          ts: Date.now(),
        }));
      } catch (_) {}
    }
  }, [searchParams]);

  useEffect(() => {
    const els = document.querySelectorAll('.naplex-animate');
    if (!els.length) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('naplex-visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const handleCta = () => {
    if (isAuthenticated) {
      if (user?.mpje_dlc) {
        navigate('/dashboard?mpje=1');
      } else {
        startStripeCheckout('mpje_crunch', { fallbackNavigate: navigate });
      }
    } else {
      navigate('/signup?mpje=1');
    }
  };

  const fMpje = foundersFor('mpje');

  return (
    <div className="naplex-prep mpje-prep">
      <Helmet>
        <title>MPJE DLC: AI-Powered MPJE Prep | Debono RX</title>
        <meta
          name="description"
          content="6 MPJE domains with procedural question generation, inspector boss fights, citation-tracked drills, and adaptive learning maps. One-time $35 purchase, permanent access."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={robotsValue} />
        <meta property="og:title" content="MPJE DLC: AI-Powered MPJE Prep | Debono RX" />
        <meta
          property="og:description"
          content="6 MPJE domains. Unlimited generated questions. Inspector boss fights. $35 one-time, permanent access."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Debono RX" />
        <meta property="og:image" content="https://debono.ai/og/mpje.png?v=1" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://debono.ai/og/mpje.png?v=1" />
      </Helmet>

      <div className="naplex-prep-bg">
        <div className="naplex-prep-glow naplex-prep-glow-1" />
        <div className="naplex-prep-glow naplex-prep-glow-2" />
        <div className="naplex-prep-glow naplex-prep-glow-3" />
      </div>

      <nav className="naplex-prep-topbar">
        <div className="naplex-prep-topbar-container">
          <Link to="/" className="naplex-prep-topbar-logo">
            <span className="naplex-prep-topbar-bono">DEBONO</span>
            <img src={`${process.env.PUBLIC_URL}/favicon-32x32.png?v=7`} alt="" className="naplex-prep-topbar-favicon" />
            <span className="naplex-prep-topbar-rx">rx</span>
          </Link>
          <div className="naplex-prep-topbar-links">
            <Link to="/pricing">Pricing</Link>
            {isAuthenticated ? (
              <Link to="/dashboard?mpje=1" className="naplex-prep-topbar-cta">Dashboard</Link>
            ) : (
              <>
                <Link to="/login">Log In</Link>
                <Link to="/signup?mpje=1" className="naplex-prep-topbar-cta">Sign Up</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="naplex-prep-inner">
        <section className="naplex-prep-hero">
          <div className="naplex-prep-badge naplex-animate">Permanent DLC · One-Time Purchase</div>
          {fMpje && (
            <div className="naplex-prep-founders-banner naplex-animate" role="status" aria-live="polite">
              <span className="naplex-prep-founders-dot" />
              <span className="naplex-prep-founders-label">Founders Pricing</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-was">${fMpje.orig}</span>
              <span className="naplex-prep-founders-now">${fMpje.sale}</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-slots">first {fMpje.slots} only</span>
            </div>
          )}
          <h1 className="naplex-animate">
            <span>MPJE Prep</span>, Rewired
          </h1>
          <p className="naplex-prep-hero-sub naplex-animate">
            Six testable MPJE domains, rebuilt as a live compliance board. Train controlled
            substances, federal law, prescription rules, operations, safety, and regulatory
            structure with fresh AI-generated questions, citation-tracked drills, and inspector
            walkthroughs that lock onto what you missed.
          </p>
          <div className="naplex-prep-hero-cta naplex-animate">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.mpje_dlc
                ? 'Already Unlocked'
                : `Get MPJE DLC · $${fMpje ? fMpje.sale : 35}`}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              See All Plans
            </Link>
          </div>
        </section>

        <div className="naplex-prep-stats naplex-animate">
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">6</span>
            <span className="naplex-prep-stat-label">Exam Domains</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">21 CFR</span>
            <span className="naplex-prep-stat-label">Citation-Indexed</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">{'\u221E'}</span>
            <span className="naplex-prep-stat-label">Generated Questions</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">
              {fMpje && <span className="naplex-prep-stat-was">${fMpje.orig}</span>}
              ${fMpje ? fMpje.sale : 35}
            </span>
            <span className="naplex-prep-stat-label">
              {fMpje ? 'Founders · One-Time, Forever' : 'One-Time, Forever'}
            </span>
          </div>
        </div>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>How It Works</h2>
            <p>
              Start with a diagnostic hit. Fail fast, surface the exact domain gaps, then
              drill back through a focused map until the workflow is clean.
            </p>
          </div>

          <div className="naplex-prep-flow">
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--1">1</div>
              <h3>Diagnostic Board Inspection</h3>
              <p>
                Choose an MPJE domain and walk in cold. Controlled substances, federal law,
                prescription requirements, operations, safety, and regulatory structure all get
                audited before the map opens.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--2">2</div>
              <h3>Targeted Citation Map</h3>
              <p>
                Every miss generates a focused recovery route keyed to the exact statute or
                CFR section you missed. You study only the rules that actually broke your run,
                with citation drill-downs on demand.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--3">3</div>
              <h3>Inspector Clearance</h3>
              <p>
                Clear domain inspectors, cross-domain synthesis audits, and final compliance
                checks that force accurate citation recall, timeline precision, schedule rules,
                and violation triage under pressure.
              </p>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>MPJE Scope. No Dead Repeats.</h2>
            <p>
              The NABP blueprint plus federal law core, broken into six gameable domains. Fresh
              question generations, adaptive retrieval, and inspector encounters that change
              every run, with statute-level citation tracking baked in.
            </p>
          </div>

          <div className="naplex-prep-yield">
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">High Yield</span>
                Controlled Substances
              </h3>
              <p>The DEA content that drives the largest share of the exam.</p>
              <ul className="naplex-prep-yield-list">
                <li>Schedules II-V rules, prescription requirements, refill windows</li>
                <li>DEA Forms 222 / 41 / 106: when each applies and retention periods</li>
                <li>Biennial and perpetual inventory, lost/stolen reporting</li>
                <li>Emergency, partial, and faxed-fill rules by schedule</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--medium">Medium Yield</span>
                Federal Law & Operations
              </h3>
              <p>The statutes and USP standards you need to cite, not just recognize.</p>
              <ul className="naplex-prep-yield-list">
                <li>FDCA, OBRA-90, HIPAA 164: the sections actually tested</li>
                <li>USP 795 / 797 / 800 compounding + BUD determination</li>
                <li>Pharmacist-in-charge duties, staffing, and supervision rules</li>
                <li>Licensure, reciprocity, and non-resident pharmacy requirements</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--low">Low Yield</span>
                Regulatory Precision
              </h3>
              <p>The day-counts and citation edges that quietly decide the exam.</p>
              <ul className="naplex-prep-yield-list">
                <li>Timeline drills: 72-hour emergency fills, 7-day C-II fax follow-up, PDMP windows</li>
                <li>Record retention: 2-year CSA, 5-year Medicare, 10-year state variations</li>
                <li>REMS programs, black-box warnings, MedWatch reporting triggers</li>
                <li>State-versus-federal preemption and board disciplinary framework</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">Adaptive</span>
                Gap Targeting
              </h3>
              <p>The system prioritizes your actual misses instead of forcing a full reset every time.</p>
              <ul className="naplex-prep-yield-list">
                <li>Failed questions immediately feed the next learning map</li>
                <li>Boss sessions escalate into the areas you keep missing</li>
                <li>Free-domain entry lets you sample one domain before committing</li>
                <li>Retry runs stay fresh instead of recycling a dead bank</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>6 Exam Domains</h2>
            <p>
              The full MPJE outline, each domain with its own lessons, cross-check nodes,
              boss layers, and scenario rounds.
            </p>
          </div>

          <div className="naplex-prep-areas-grid">
            {DOMAINS.map((domain) => (
              <div
                key={domain.name}
                className="naplex-prep-area-card naplex-animate"
                style={{ '--area-color': domain.color }}
              >
                <div className="naplex-prep-area-card-glow" />
                <div className="naplex-prep-area-header">
                  <div className="naplex-prep-area-dot" />
                  <div className="naplex-prep-area-name">{domain.name}</div>
                </div>
                <div className="naplex-prep-area-nodes">
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.lessons} teaching lessons`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                    <span>{domain.lessons}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.synthesis} synthesis nodes`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    <span>{domain.synthesis}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.bosses} boss fights`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span>{domain.bosses}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.rounds} scenario rounds`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                    <span>{domain.rounds}</span>
                  </div>
                </div>
              </div>
            ))}
            <div
              className="naplex-prep-area-card naplex-prep-area-card--totals naplex-animate"
              style={{ '--area-color': '#3b82f6' }}
            >
              <div className="naplex-prep-area-card-glow" />
              <div className="naplex-prep-area-header">
                <div className="naplex-prep-area-dot" />
                <div className="naplex-prep-area-name">Total Coverage</div>
              </div>
              <div className="naplex-prep-area-nodes">
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.lessons, 0)}</span>
                  <span className="naplex-prep-area-node--label">lessons</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.synthesis, 0)}</span>
                  <span className="naplex-prep-area-node--label">links</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.bosses, 0)}</span>
                  <span className="naplex-prep-area-node--label">bosses</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.rounds, 0)}</span>
                  <span className="naplex-prep-area-node--label">rounds</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  <span>6</span>
                  <span className="naplex-prep-area-node--label">domains</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3"/></svg>
                  <span>877</span>
                  <span className="naplex-prep-area-node--label">topics</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                  <span>18</span>
                  <span className="naplex-prep-area-node--label">safety flags</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5"/></svg>
                  <span>{'\u221E'}</span>
                  <span className="naplex-prep-area-node--label">questions</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>What You Encounter</h2>
            <p>
              The same stacked-glass combat rhythm as the NAPLEX product, tuned for retail workflow,
              law precision, medication recall, and safety verification.
            </p>
          </div>

          <div className="naplex-prep-nodes">
            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(59, 130, 246, 0.14)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Diagnostic Boss Openers</h3>
              <p>
                Fast domain checks that expose weak law recall, safety misses,
                order-entry mistakes, and top-200 holes before the learning map opens.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(14, 165, 233, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                </svg>
              </div>
              <h3>Medication Mastery Sessions</h3>
              <p>
                Focused review on brand/generic mapping, schedules, dosage forms,
                high-alert meds, and calculation-heavy retrieval.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(129, 140, 248, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </div>
              <h3>Workflow Synthesis</h3>
              <p>
                Cross-step nodes for input, fill, verify, and counsel sequences.
                Learn the process, not just isolated facts.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(34, 211, 238, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3>Inspector Walkthroughs</h3>
              <p>
                Force the jurisprudence mindset: spot violations, cite the exact rule broken,
                triage corrective action, and survive a surprise board audit.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(96, 165, 250, 0.12)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <h3>Scenario Rounds</h3>
              <p>
                Applied patient and operations cases: refill too soon, inventory
                conflict, insurance rejection, or compounding workflow breakdowns.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: 'rgba(37, 99, 235, 0.14)' }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <h3>Boss Fights</h3>
              <p>
                Domain bosses, synthesis bosses, and final workflow clashes that turn
                the exam blueprint into a repeatable combat loop instead of a dead bank.
              </p>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>Not Another Static MPJE Bank</h2>
            <p>
              Most MPJE prep tools become memory drills once you finish the bank.
              This stays procedural: new scenarios, new citations, same blueprint.
            </p>
          </div>

          <div className="naplex-prep-compare">
            <div className="naplex-prep-compare-card naplex-prep-compare-card--them naplex-animate">
              <div className="naplex-prep-compare-label">Traditional Banks</div>
              <ul className="naplex-prep-compare-list">
                <li><XIcon /> Fixed question pool that gets stale fast</li>
                <li><XIcon /> Weak workflow simulation and almost no pressure feedback</li>
                <li><XIcon /> Generic review that treats every miss the same</li>
                <li><XIcon /> No citation tracking. You memorize rules without knowing which statute</li>
                <li><XIcon /> Monthly renewals just to keep drilling</li>
              </ul>
            </div>
            <div className="naplex-prep-compare-card naplex-prep-compare-card--us naplex-animate">
              <div className="naplex-prep-compare-label">Debono MPJE DLC</div>
              <ul className="naplex-prep-compare-list">
                <li><CheckIcon /> AI generates fresh questions from seeded domain content</li>
                <li><CheckIcon /> Boss fights and maps adapt to your actual weak spots</li>
                <li><CheckIcon /> Workflow logic, law, and meds stay connected</li>
                <li><CheckIcon /> One free domain to test the loop before you unlock all six</li>
                <li><CheckIcon /> {fMpje ? `$${fMpje.sale} founders price (was $${fMpje.orig})` : '$35'} once for permanent access</li>
              </ul>
            </div>
          </div>
        </section>

        <MobileDesktopBanner
          page="mpje-prep"
          headline="Debono's MPJE prep works best on desktop."
          subtext="Drop your email and we'll send you the link. Takes 2 seconds."
        />

        <section className="naplex-prep-bottom-cta naplex-animate">
          <h2>One price. Six MPJE domains. Unlimited replays.</h2>
          <p>
            Permanent access to medications, law, safety, and order-entry training without a recurring bill.
          </p>
          <div className="naplex-prep-price-display">
            {fMpje && <span className="naplex-prep-price-was">${fMpje.orig}</span>}
            <span className="naplex-prep-price-amount">${fMpje ? fMpje.sale : 35}</span>
            <span className="naplex-prep-price-period">one-time</span>
          </div>
          {fMpje && (
            <div className="naplex-prep-founders-banner naplex-prep-founders-banner--centered">
              <span className="naplex-prep-founders-dot" />
              <span className="naplex-prep-founders-label">Founders price</span>
              <span className="naplex-prep-founders-sep" aria-hidden="true">·</span>
              <span className="naplex-prep-founders-slots">first {fMpje.slots} only</span>
            </div>
          )}
          <p className="naplex-prep-price-note">
            Includes all six MPJE domains. Start with one free domain, then unlock the full board.
          </p>
          <div className="naplex-prep-hero-cta">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.mpje_dlc ? 'Already Unlocked' : (isAuthenticated ? 'Get MPJE DLC' : 'Get Started')}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              Compare Plans
            </Link>
          </div>
        </section>

        <footer className="naplex-prep-footer">
          <div className="naplex-prep-footer-links">
            <Link to="/">Home</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
